import { DOCUMENT_DEFINITIONS } from '@/features/case-workspace/schemas'
import { CBP_7501_LINE_ITEM_FIELDS } from '@/features/case-workspace/cbp7501'
import { deriveCbp7501DocumentAutomation } from '@/features/case-workspace/cbp7501LineAutomation'
import { buildCbp7501FieldStateEngine } from '@/features/case-workspace/cbp7501FieldState'
import { validateCbp7501SchemaRules } from '@/features/case-workspace/cbp7501Validation'
import { COMMERCIAL_INVOICE_LINE_ITEM_FIELDS } from '@/features/case-workspace/invoiceTemplate'
import { MANIFEST_1302_SHIPMENT_FIELDS } from '@/features/case-workspace/manifest1302'
import type {
  CaseReviewState,
  DocumentFieldState,
  DocumentCompletenessStatus,
  DocumentReviewSummary,
  DocumentFieldDefinition,
  FieldValidationIssue,
  ReadinessCheck,
  SectionReviewStatus,
  SourceDocumentRecord,
  StructuredDocumentRecord,
} from '@/features/case-workspace/types'

export function validateStructuredDocument(record: StructuredDocumentRecord) {
  const definition = DOCUMENT_DEFINITIONS[record.documentType]
  const issues: FieldValidationIssue[] = []
  const lineItems = record.lineItems ?? []
  const cbp7501FieldState = buildCbp7501FieldStateEngine(record)

  for (const section of definition.sections) {
    if (section.kind === 'line_items') {
      const lineItemFields =
        record.documentType === 'cbp_7501'
          ? CBP_7501_LINE_ITEM_FIELDS
          : record.documentType === 'manifest'
            ? MANIFEST_1302_SHIPMENT_FIELDS
            : record.documentType === 'commercial_invoice'
              ? COMMERCIAL_INVOICE_LINE_ITEM_FIELDS
            : section.fields
      for (const lineItem of lineItems) {
        for (const field of lineItemFields) {
          const runtimeState = cbp7501FieldState?.lineItemFields[lineItem.id]?.[field.key]
          if (runtimeState?.disabled && !runtimeState.overrideEnabled) continue
          const fieldState = lineItem.fields[field.key]
          const value = getLineItemValidationValue(record, lineItem.fields, field.key)
          issues.push(
            ...validateField(field, value, record.fields, fieldState, `${field.key}:${lineItem.id}`),
          )
        }
      }
      continue
    }

    for (const field of section.fields) {
      if (!isFieldVisible(field, record.fields)) continue
      const runtimeState = cbp7501FieldState?.topLevelFields[field.key]
      if (runtimeState?.disabled && !runtimeState.overrideEnabled) continue
      const fieldState = record.fields[field.key]
      const value = fieldState?.value?.trim() ?? ''
      issues.push(...validateField(field, value, record.fields, fieldState))
    }
  }

  issues.push(...validate7501Totals(record))
  issues.push(...validateCbp7501SchemaRules(record))
  issues.push(...validateManifestDocument(record))
  issues.push(...validateCommercialInvoiceDocument(record))

  return issues
}

function getLineItemValidationValue(
  record: StructuredDocumentRecord,
  lineItemFields: Record<string, DocumentFieldState>,
  fieldKey: string,
) {
  const value = lineItemFields[fieldKey]?.value?.trim() ?? ''
  if (record.documentType === 'cbp_7501' && fieldKey === 'relationship') {
    return value || (record.fields.relationship_global?.value?.trim() ?? '')
  }
  return value
}

function evaluateCondition(
  condition: NonNullable<DocumentFieldDefinition['visibleWhen']>[number],
  fields: Record<string, DocumentFieldState>,
) {
  const targetValue = fields[condition.fieldKey]?.value?.trim() ?? ''
  if (condition.operator === 'not_empty') {
    return targetValue.length > 0
  }
  if (condition.operator === 'equals') {
    return targetValue === condition.value
  }
  if (condition.operator === 'one_of') {
    const expectedValues = Array.isArray(condition.value) ? condition.value : [condition.value ?? '']
    return expectedValues.includes(targetValue)
  }
  return false
}

function isFieldRequired(
  field: DocumentFieldDefinition,
  fields: Record<string, DocumentFieldState>,
) {
  const isRequiredByCondition = (field.requiredWhen ?? []).every((condition) => evaluateCondition(condition, fields))
  return field.required || ((field.requiredWhen?.length ?? 0) > 0 && isRequiredByCondition)
}

export function isFieldVisible(
  field: DocumentFieldDefinition,
  fields: Record<string, DocumentFieldState>,
) {
  if (!field.visibleWhen || field.visibleWhen.length === 0) return true
  return field.visibleWhen.every((condition) => evaluateCondition(condition, fields))
}

function validateField(
  field: DocumentFieldDefinition,
  value: string,
  fields: Record<string, DocumentFieldState>,
  fieldState?: DocumentFieldState,
  overrideKey?: string,
): FieldValidationIssue[] {
  const issues: FieldValidationIssue[] = []
  const fieldKey = overrideKey ?? field.key
  const validations = field.validation ?? []
  const hasExplicitRequiredRule = validations.some((rule) => rule.type === 'required')
  const isRequired = isFieldRequired(field, fields)

  if (isRequired && !value && !hasExplicitRequiredRule) {
    issues.push({
      fieldKey,
      severity: 'error',
      message: `${field.label} is required.`,
    })
  }

  for (const rule of validations) {
    if (rule.type === 'required' && !value) {
      issues.push({ fieldKey, severity: 'error', message: rule.message })
    }
    if (rule.type === 'pattern' && value) {
      const pattern = typeof rule.value === 'string' ? new RegExp(rule.value) : null
      if (pattern && !pattern.test(value)) {
        issues.push({ fieldKey, severity: 'warning', message: rule.message })
      }
    }
    if (rule.type === 'min' && value) {
      const numericValue = Number(value)
      if (!Number.isNaN(numericValue) && typeof rule.value === 'number' && numericValue < rule.value) {
        issues.push({ fieldKey, severity: 'warning', message: rule.message })
      }
    }
    if (rule.type === 'max' && value) {
      const numericValue = Number(value)
      if (!Number.isNaN(numericValue) && typeof rule.value === 'number' && numericValue > rule.value) {
        issues.push({ fieldKey, severity: 'warning', message: rule.message })
      }
    }
    if (rule.type === 'custom' && value) {
      if (field.key === 'missing_docs' && !isValidMissingDocs(value)) {
        issues.push({ fieldKey, severity: 'warning', message: rule.message })
      }
      if (field.key === 'container_numbers' && !areContainerNumbersValid(value)) {
        issues.push({ fieldKey, severity: 'warning', message: rule.message })
      }
      if (field.key === 'it_date' && fieldState && fields.import_date?.value && !isItDateValid(fieldState.value, fields.import_date.value)) {
        issues.push({
          fieldKey,
          severity: 'warning',
          message: 'I.T. date cannot be earlier than the import date.',
        })
      }
    }
  }

  return issues
}

function parseNumber(value: string) {
  const numericValue = Number(value)
  return Number.isFinite(numericValue) ? numericValue : 0
}

function isValidMissingDocs(value: string) {
  const codes = value
    .split(',')
    .map((code) => code.trim())
    .filter(Boolean)
  if (codes.length === 0 || codes.length > 2) return false
  const validCodes = new Set(['01', '10', '14', '15', '16', '17', '18', '19', '20', '21', '22', '23', '26', '98', '99'])
  if (!codes.every((code) => validCodes.has(code))) return false
  if (codes.includes('99') && codes.length !== 2) return false
  return true
}

function asDate(value: string) {
  const match = value.match(/^(\d{2})\/(\d{2})\/(\d{4})$/)
  if (!match) return null
  const [, month, day, year] = match
  const date = new Date(`${year}-${month}-${day}T00:00:00Z`)
  if (Number.isNaN(date.getTime())) return null
  // Reject calendar rollover (e.g. 02/30/2026 → Mar 2) so invalid dates
  // don't sneak past the regex via Date's overflow behavior.
  if (
    date.getUTCFullYear() !== Number(year)
    || date.getUTCMonth() + 1 !== Number(month)
    || date.getUTCDate() !== Number(day)
  ) {
    return null
  }
  return date
}

function isItDateValid(itDate: string, importDate: string) {
  const parsedItDate = asDate(itDate)
  const parsedImportDate = asDate(importDate)
  if (!parsedItDate || !parsedImportDate) return true
  return parsedItDate >= parsedImportDate
}

function areContainerNumbersValid(value: string) {
  const numbers = value
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean)
  if (numbers.length === 0) return true
  return numbers.every((number) => /^[A-Z]{4}\d{7}$/.test(number.replace(/\s+/g, '').toUpperCase()))
}

function validate7501Totals(record: StructuredDocumentRecord) {
  if (record.documentType !== 'cbp_7501') return []

  const issues: FieldValidationIssue[] = []
  const lineItems = record.lineItems ?? []
  const calculated = deriveCbp7501DocumentAutomation(record, record.lastUpdatedBy || 'System')
  const calculatedEnteredValue = parseNumber(calculated.totalEnteredValue)
  const calculatedDuty = parseNumber(calculated.dutyTotal)
  const calculatedTax = parseNumber(calculated.taxTotal)
  const calculatedOther = parseNumber(calculated.otherTotal)
  const calculatedGrandTotal = parseNumber(calculated.grandTotal)
  const declaredEnteredValue = parseNumber(record.fields.total_entered_value?.value ?? '')
  const declaredGrandTotal = parseNumber(record.fields.grand_total?.value ?? '')
  const declaredDuty = parseNumber(record.fields.duty_total?.value ?? '')
  const declaredTax = parseNumber(record.fields.tax_total?.value ?? '')
  const declaredOther = parseNumber(record.fields.other_total?.value ?? '')
  const hasManualOverrideMismatch =
    (record.fields.total_entered_value?.origin !== 'system' && Math.abs(calculatedEnteredValue - declaredEnteredValue) > 0.01)
    || (record.fields.duty_total?.origin !== 'system' && Math.abs(calculatedDuty - declaredDuty) > 0.01)
    || (record.fields.tax_total?.origin !== 'system' && Math.abs(calculatedTax - declaredTax) > 0.01)
    || (record.fields.other_total?.origin !== 'system' && Math.abs(calculatedOther - declaredOther) > 0.01)
    || (record.fields.grand_total?.origin !== 'system' && Math.abs(calculatedGrandTotal - declaredGrandTotal) > 0.01)

  if (lineItems.length > 0 && Math.abs(calculatedEnteredValue - declaredEnteredValue) > 0.01) {
    issues.push({
      fieldKey: 'total_entered_value',
      severity: 'warning',
      message: `Block 39 does not match the current line-item total (${calculatedEnteredValue.toFixed(2)}).`,
    })
  }

  if (lineItems.length > 0 && Math.abs(calculatedDuty - declaredDuty) > 0.01) {
    issues.push({
      fieldKey: 'duty_total',
      severity: 'warning',
      message: `Duty total does not match the current line-item duty calculation (${calculatedDuty.toFixed(2)}).`,
    })
  }

  if (lineItems.length > 0 && Math.abs(calculatedTax - declaredTax) > 0.01) {
    issues.push({
      fieldKey: 'tax_total',
      severity: 'warning',
      message: `Tax total does not match the current line-item IRC tax calculation (${calculatedTax.toFixed(2)}).`,
    })
  }

  if (lineItems.length > 0 && Math.abs(calculatedOther - declaredOther) > 0.01) {
    issues.push({
      fieldKey: 'other_total',
      severity: 'warning',
      message: `Other total does not match the current line-item fee calculation (${calculatedOther.toFixed(2)}).`,
    })
  }

  if (Math.abs(calculatedGrandTotal - declaredGrandTotal) > 0.01) {
    issues.push({
      fieldKey: 'grand_total',
      severity: 'warning',
      message: `Block 44 does not equal the current duty, tax, and other totals (${calculatedGrandTotal.toFixed(2)}).`,
    })
  }

  if (hasManualOverrideMismatch && !(record.fields.totals_override_reason?.value?.trim())) {
    issues.push({
      fieldKey: 'totals_override_reason',
      severity: 'warning',
      message: 'Add a totals override reason when a calculated total is manually overridden.',
    })
  }

  return issues
}

function validateManifestDocument(record: StructuredDocumentRecord) {
  if (record.documentType !== 'manifest') return []

  const issues: FieldValidationIssue[] = []
  const lineItems = record.lineItems ?? []
  const intakeSource = record.fields.manifest_intake_source?.value ?? ''

  if (!record.fields.manifest_number?.value?.trim()) {
    issues.push({
      fieldKey: 'manifest_number',
      severity: 'error',
      message: 'Manifest number is required.',
    })
  }

  if (!record.fields.vessel_name?.value?.trim()) {
    issues.push({
      fieldKey: 'vessel_name',
      severity: 'error',
      message: 'Vessel name is required.',
    })
  }

  if (!record.fields.last_foreign_port_before_us?.value?.trim()) {
    issues.push({
      fieldKey: 'last_foreign_port_before_us',
      severity: 'warning',
      message: 'Last foreign port before the U.S. should be confirmed from the manifest.',
    })
  }

  if (!record.fields.us_port_of_discharge?.value?.trim()) {
    issues.push({
      fieldKey: 'us_port_of_discharge',
      severity: 'error',
      message: 'U.S. port of discharge is required.',
    })
  }

  if (intakeSource === 'port_arrival_document') {
    const paperCriticalFields = ['manifest_number', 'vessel_name', 'arrival_date', 'last_foreign_port_before_us', 'us_port_of_discharge']
    for (const fieldKey of paperCriticalFields) {
      if (!record.fields[fieldKey]?.value?.trim()) {
        issues.push({
          fieldKey,
          severity: 'warning',
          message: 'This field should be confirmed when the manifest was provided on arrival at the port.',
        })
      }
    }
  }

  if (lineItems.length === 0) {
    issues.push({
      fieldKey: 'shipment_registry',
      severity: 'error',
      message: 'At least one manifest shipment row is required.',
    })
  }

  for (const shipment of lineItems) {
    if (!shipment.fields.bill_of_lading_number?.value?.trim()) {
      issues.push({
        fieldKey: `bill_of_lading_number:${shipment.id}`,
        severity: 'error',
        message: 'Bill of lading number is required for each shipment row.',
      })
    }
    if (!shipment.fields.goods_description?.value?.trim()) {
      issues.push({
        fieldKey: `goods_description:${shipment.id}`,
        severity: 'error',
        message: 'Description of goods is required for each shipment row.',
      })
    }
    if (!shipment.fields.consignee_name?.value?.trim()) {
      issues.push({
        fieldKey: `consignee_name:${shipment.id}`,
        severity: 'warning',
        message: 'Consignee should be reviewed for each shipment row.',
      })
    }
    if (!shipment.fields.shipper_name?.value?.trim()) {
      issues.push({
        fieldKey: `shipper_name:${shipment.id}`,
        severity: 'warning',
        message: 'Shipper should be reviewed for each shipment row.',
      })
    }
  }

  return issues
}

function validateCommercialInvoiceDocument(record: StructuredDocumentRecord) {
  if (record.documentType !== 'commercial_invoice') return []

  const issues: FieldValidationIssue[] = []
  const lineItems = record.lineItems ?? []
  const source = record.fields.document_source?.value ?? ''
  const declaredInvoiceValue = parseNumber(record.fields.total_invoice_value?.value ?? '')
  const declaredChargeTotal = parseNumber(record.fields.total_charges?.value ?? '')
  const calculatedInvoiceValue = lineItems.reduce(
    (total, lineItem) => total + parseNumber(lineItem.fields.extended_line_value?.value ?? ''),
    0,
  )
  const calculatedChargeTotal =
    parseNumber(record.fields.freight_charge?.value ?? '') +
    parseNumber(record.fields.insurance_charge?.value ?? '') +
    parseNumber(record.fields.commission_charge?.value ?? '') +
    parseNumber(record.fields.cases_charge?.value ?? '') +
    parseNumber(record.fields.containers_charge?.value ?? '') +
    parseNumber(record.fields.coverings_charge?.value ?? '') +
    parseNumber(record.fields.packing_cost?.value ?? '') +
    parseNumber(record.fields.inland_freight_charge?.value ?? '') +
    parseNumber(record.fields.international_freight_charge?.value ?? '') +
    parseNumber(record.fields.royalties_license_fees?.value ?? '')

  const requiredTopLevelFields = [
    ['invoice_number', 'Invoice number is required.'],
    ['invoice_date', 'Invoice date is required.'],
    ['currency_code', 'Currency is required.'],
    ['seller_name', 'Seller name is required.'],
    ['buyer_name', 'Buyer name is required.'],
    ['country_of_origin', 'Country of origin should be confirmed for invoice review.'],
    ['port_of_entry_destination', 'Port of entry destination should be confirmed for invoice review.'],
  ] as const

  for (const [fieldKey, message] of requiredTopLevelFields) {
    if (!record.fields[fieldKey]?.value?.trim()) {
      issues.push({
        fieldKey,
        severity:
          fieldKey === 'country_of_origin' || fieldKey === 'port_of_entry_destination'
            ? 'warning'
            : 'error',
        message,
      })
    }
  }

  if (source === 'uploaded_scan' && record.fields.ocr_confidence?.value) {
    const confidence = Number(record.fields.ocr_confidence.value)
    if (Number.isFinite(confidence) && confidence < 0.8) {
      issues.push({
        fieldKey: 'ocr_confidence',
        severity: 'warning',
        message: 'OCR confidence is low for this invoice. Review the extracted values carefully.',
      })
    }
  }

  if (lineItems.length === 0) {
    issues.push({
      fieldKey: 'invoice_lines_registry',
      severity: 'error',
      message: 'At least one structured merchandise line is required.',
    })
  }

  for (const lineItem of lineItems) {
    if (!lineItem.fields.detailed_merchandise_description?.value?.trim()) {
      issues.push({
        fieldKey: `detailed_merchandise_description:${lineItem.id}`,
        severity: 'error',
        message: 'Each merchandise line needs a detailed description.',
      })
    }
    if (!lineItem.fields.quantity_uom?.value?.trim()) {
      issues.push({
        fieldKey: `quantity_uom:${lineItem.id}`,
        severity: 'warning',
        message: 'Each merchandise line should identify the quantity unit of measure.',
      })
    }

    const quantity = parseNumber(lineItem.fields.quantity?.value ?? '')
    const unitPrice = parseNumber(lineItem.fields.unit_price?.value ?? '')
    const extendedValue = parseNumber(lineItem.fields.extended_line_value?.value ?? '')
    if (quantity > 0 && unitPrice > 0 && Math.abs(quantity * unitPrice - extendedValue) > 0.01) {
      issues.push({
        fieldKey: `extended_line_value:${lineItem.id}`,
        severity: 'warning',
        message: 'Extended line value does not match quantity multiplied by unit price.',
      })
    }
  }

  if (lineItems.length > 0 && Math.abs(calculatedInvoiceValue - declaredInvoiceValue) > 0.01) {
    issues.push({
      fieldKey: 'total_invoice_value',
      severity: 'warning',
      message: `Total invoice value does not match the current merchandise line total (${calculatedInvoiceValue.toFixed(2)}).`,
    })
  }

  if (Math.abs(calculatedChargeTotal - declaredChargeTotal) > 0.01) {
    issues.push({
      fieldKey: 'total_charges',
      severity: 'warning',
      message: `Total charges do not match the current itemized charges (${calculatedChargeTotal.toFixed(2)}).`,
    })
  }

  return issues
}

function summarizeCompleteness(record: StructuredDocumentRecord): DocumentCompletenessStatus {
  const lineItems = record.lineItems ?? []
  const hasManualUpdates = Object.values(record.fields).some((field) => field.origin === 'manual' || field.origin === 'reviewed')
  const hasAutoPopulatedValues = Object.values(record.fields).some((field) => field.origin === 'ai_extracted')
  const hasLineItemActivity = lineItems.some((lineItem) =>
    Object.values(lineItem.fields).some((field) => field.value.trim().length > 0),
  )

  if (record.status === 'complete') return 'complete'
  if (record.status === 'ready') return 'reviewed'
  if (record.status === 'missing') return 'not_started'
  if (record.status === 'needs_attention') return 'incomplete'
  if (hasLineItemActivity) return 'in_progress'
  if (hasManualUpdates) return 'in_progress'
  if (hasAutoPopulatedValues) return 'auto_populated'
  return 'not_started'
}

export function summarizeSectionStatus(record: StructuredDocumentRecord, sectionId: string): SectionReviewStatus {
  const definition = DOCUMENT_DEFINITIONS[record.documentType]
  const section = definition.sections.find((item) => item.id === sectionId)
  if (!section) return 'not_started'
  const lineItems = record.lineItems ?? []
  const explicitState = record.sectionStates?.[sectionId]

  if (section.kind === 'line_items') {
    if (lineItems.length === 0) return 'not_started'
    const lineIssues = record.validationIssues.filter((issue) => issue.fieldKey.includes(':'))
    if (lineIssues.some((issue) => issue.severity === 'error')) return 'validation_error'
    if (record.documentType === 'manifest' || record.documentType === 'commercial_invoice') {
      const shipmentRecommendations = record.awaRecommendations.filter((recommendation) => recommendation.state !== 'resolved')
      if (shipmentRecommendations.length > 0) return 'cross_document_conflict'
    }
    if (lineIssues.length > 0) return 'needs_review'
    if (explicitState?.status === 'complete') return 'complete'
    if (lineItems.every((lineItem) =>
      Object.values(lineItem.fields).some((field) => field.value.trim().length > 0),
    )) {
      return 'in_progress'
    }
    return 'not_started'
  }

  const visibleFields = section.fields.filter((field) => isFieldVisible(field, record.fields))
  const populatedCount = visibleFields.filter((field) => record.fields[field.key]?.value?.trim()).length
  const sectionIssues = record.validationIssues.filter((issue) =>
    visibleFields.some((field) => field.key === issue.fieldKey),
  )
  const sectionRecommendations = record.awaRecommendations.filter(
    (recommendation) =>
      recommendation.state !== 'resolved'
      && visibleFields.some((field) => field.key === recommendation.fieldKey)
      && (record.documentType === 'manifest' || record.documentType === 'commercial_invoice'),
  )

  if (sectionIssues.some((issue) => issue.severity === 'error')) return 'validation_error'
  if (sectionRecommendations.length > 0) return 'cross_document_conflict'
  if (sectionIssues.length > 0) return 'needs_review'
  if (explicitState?.status === 'complete') return 'complete'
  if (visibleFields.length > 0 && populatedCount === 0) return 'not_started'
  return 'in_progress'
}

export function canMarkSectionComplete(record: StructuredDocumentRecord, sectionId: string) {
  const definition = DOCUMENT_DEFINITIONS[record.documentType]
  const section = definition.sections.find((item) => item.id === sectionId)
  if (!section) return false

  if (section.kind === 'line_items') {
    const lineItems = record.lineItems ?? []
    if (lineItems.length === 0) return false

    const lineItemFields =
      record.documentType === 'cbp_7501'
        ? CBP_7501_LINE_ITEM_FIELDS
        : record.documentType === 'manifest'
          ? MANIFEST_1302_SHIPMENT_FIELDS
          : record.documentType === 'commercial_invoice'
            ? COMMERCIAL_INVOICE_LINE_ITEM_FIELDS
            : section.fields

    const hasLineItemValidationIssues = record.validationIssues.some((issue) => issue.fieldKey.includes(':'))
    if (hasLineItemValidationIssues) return false

    return lineItems.every((lineItem) =>
      lineItemFields
        .filter((field) => isFieldRequired(field, lineItem.fields))
        .every((field) => (lineItem.fields[field.key]?.value?.trim() ?? '').length > 0),
    )
  }

  const visibleFields = section.fields.filter((field) => isFieldVisible(field, record.fields))
  const requiredFields = visibleFields.filter((field) => isFieldRequired(field, record.fields))
  const hasValidationIssues = record.validationIssues.some((issue) =>
    visibleFields.some((field) => field.key === issue.fieldKey),
  )

  if (hasValidationIssues) return false

  return requiredFields.every((field) => (record.fields[field.key]?.value?.trim() ?? '').length > 0)
}

export function canMarkDocumentComplete(record: StructuredDocumentRecord) {
  if (record.validationIssues.length > 0) return false
  return DOCUMENT_DEFINITIONS[record.documentType].sections.every((section) =>
    canMarkSectionComplete(record, section.id),
  )
}

function summarizeDocumentNote(record: StructuredDocumentRecord, sourceCount: number) {
  const blockingIssues = record.validationIssues.filter((issue) => issue.severity === 'error').length
  const warningIssues = record.validationIssues.filter((issue) => issue.severity === 'warning').length

  if (blockingIssues > 0) {
    return `${blockingIssues} required field${blockingIssues === 1 ? '' : 's'} still need attention.`
  }
  if (record.status === 'complete') {
    return sourceCount > 0
      ? 'Required information is in place and the document is marked complete.'
      : 'The document is complete and ready for review.'
  }
  if (record.status === 'needs_attention') {
    return 'This document needs follow up before the case can move cleanly through review.'
  }
  if (warningIssues > 0) {
    return `${warningIssues} warning${warningIssues === 1 ? '' : 's'} should be checked during review.`
  }
  if (sourceCount > 0) {
    return 'Supporting files are present, but the structured record still needs review.'
  }
  return 'This document record still needs to be prepared.'
}

export function buildDocumentReviewSummaries(
  records: StructuredDocumentRecord[],
  sourceDocuments: SourceDocumentRecord[],
) {
  return records.map((record) => {
    const definition = DOCUMENT_DEFINITIONS[record.documentType]
    const linkedSources = sourceDocuments.filter((source) => source.linkedStructuredDocumentId === record.id)
    const manualUpdateCount = Object.values(record.fields).filter(
      (field) => field.origin === 'manual' || field.origin === 'reviewed',
    ).length
    const autoPopulationCount = linkedSources.reduce((total, source) => total + source.autoPopulatedFieldCount, 0)
    const warningCount = record.validationIssues.length + record.awaRecommendations.filter((recommendation) => recommendation.state !== 'resolved').length

    return {
      documentId: record.id,
      documentType: record.documentType,
      title: definition.label,
      isRequired: definition.requiredForReview,
      presenceStatus: linkedSources.length > 0 || record.status !== 'missing' ? 'present' : 'missing',
      completenessStatus: summarizeCompleteness(record),
      lastUpdatedAt: record.lastUpdatedAt,
      sourceCount: linkedSources.length,
      structuredRecordStatus: record.status,
      notes: summarizeDocumentNote(record, linkedSources.length),
      autoPopulationCount,
      manualUpdateCount,
      warningCount,
    } satisfies DocumentReviewSummary
  })
}

export function evaluateReviewReadiness(records: StructuredDocumentRecord[], sourceDocuments: SourceDocumentRecord[] = []) {
  const documentSummaries = buildDocumentReviewSummaries(records, sourceDocuments)
  const checks: ReadinessCheck[] = records.map((record) => {
    const definition = DOCUMENT_DEFINITIONS[record.documentType]
    const blockingIssues = record.validationIssues.filter((issue) => issue.severity === 'error')
    const warningIssues = record.validationIssues.filter((issue) => issue.severity === 'warning')

    if (blockingIssues.length === 0 && record.status === 'complete') {
      return {
        id: record.id,
        label: definition.label,
        status: 'complete',
        detail: 'Required data is present and the record is marked complete.',
      }
    }

    if (definition.requiredForReview && blockingIssues.length > 0) {
      return {
        id: record.id,
        label: definition.label,
        status: 'missing',
        detail: `${blockingIssues.length} required field${blockingIssues.length === 1 ? '' : 's'} still need attention.`,
      }
    }

    if (definition.requiredForReview && record.status !== 'complete') {
      return {
        id: record.id,
        label: definition.label,
        status: 'missing',
        detail: 'Required record is still being prepared and must be marked complete before AWA review.',
      }
    }

    return {
      id: record.id,
      label: definition.label,
      status: warningIssues.length > 0 ? 'warning' : 'complete',
      detail:
        warningIssues.length > 0
          ? `${warningIssues.length} warning${warningIssues.length === 1 ? '' : 's'} should be reviewed before AWA analysis.`
          : 'Optional record is available for review.',
    }
  })

  const missingRequirements = checks.filter((check) => check.status === 'missing').map((check) => check.label)
  const incompleteDocuments = documentSummaries
    .filter((summary) => summary.completenessStatus !== 'complete' && summary.completenessStatus !== 'reviewed')
    .map((summary) => summary.title)
  const hasWarnings = checks.some((check) => check.status === 'warning')
  const readinessStatus =
    missingRequirements.length > 0
      ? 'missing_required_documents'
      : hasWarnings
        ? 'ready_with_warnings'
        : incompleteDocuments.length > 0
          ? 'incomplete_documents'
          : 'ready_for_review'
  const readinessSummary =
    readinessStatus === 'missing_required_documents'
      ? 'Required case documents are still missing or incomplete.'
      : readinessStatus === 'incomplete_documents'
        ? 'The case record is partly prepared, but some documents still need work before review.'
        : readinessStatus === 'ready_with_warnings'
          ? 'The case is ready for review, but a few warnings should be checked during AWA review.'
          : 'The required case documents are in place and ready for AWA review.'

  return {
    readinessStatus,
    readinessSummary,
    isReadyForReview: missingRequirements.length === 0,
    missingRequirements,
    incompleteDocuments,
    documentSummaries,
    readinessChecks: checks,
  } satisfies Pick<
    CaseReviewState,
    | 'readinessStatus'
    | 'readinessSummary'
    | 'isReadyForReview'
    | 'missingRequirements'
    | 'incompleteDocuments'
    | 'documentSummaries'
    | 'readinessChecks'
  >
}
